import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Modal } from 'antd'
import { toast } from 'sonner'
import { PageLoading } from '@/components/common/PageStates'
import { DocumentWizardShell } from '@/components/rfid/DocumentWizardShell'
import { LineItemCard } from '@/components/rfid/LineItemCard'
import { LineTagDetailDrawer } from '@/components/rfid/LineTagDetailDrawer'
import { ManualTagEntry } from '@/components/rfid/ManualTagEntry'
import { ScanSessionHero } from '@/components/rfid/ScanSessionHero'
import { WizardNavFooter } from '@/components/rfid/WizardNavFooter'
import { getMovement } from '@/config/movements'
import { useScannerTagSubscription } from '@/hooks/useScannerTagSubscription'
import { useStoreHydration } from '@/hooks/useStoreHydration'
import { rfidService } from '@/services/rfid.service'
import {
  ensureMockConnected,
  isMockScannerEnabled,
} from '@/services/scannerService'
import { useDocumentWizardStore } from '@/stores/documentWizardStore'
import { isScannerSessionActive, useRfidSessionStore } from '@/stores/rfidSessionStore'
import { getStorageLocationPlant } from '@/utils/document'
import {
  movementRequiresFromBay,
  movementRequiresFromBin,
  movementRequiresToBay,
  movementShowsShortQty,
  validateMovementLocations,
} from '@/utils/lineLocationValidation'
import { RELOCATION_FROM_BINS } from '@/config/relocationBins'
import { saveRecentDocumentScan } from '@/utils/recentScans'
import { computeScanSessionStats } from '@/utils/scanStats'
import { storageLocationDropdownOptions } from '@/utils/storageLocations'
import { allScannableComplete, countUnmatchedLeapTags } from '@/utils/tagAllocation'
import { cn } from '@/utils/cn'

export default function WizardScanPage() {
  const navigate = useNavigate()
  const hydrated = useStoreHydration(useDocumentWizardStore.persist)
  const movementId = useDocumentWizardStore((s) => s.movementId)
  const document = useDocumentWizardStore((s) => s.document)
  const lines = useDocumentWizardStore((s) => s.lines)
  const kitGroups = useDocumentWizardStore((s) => s.kitGroups)
  const kitBom = useDocumentWizardStore((s) => s.kitBom)
  const activeKitIndex = useDocumentWizardStore((s) => s.activeKitIndex)
  const tagSet = useDocumentWizardStore((s) => s.tagSet)
  const skuByTag = useDocumentWizardStore((s) => s.skuByTag)
  const leapLookupPending = useDocumentWizardStore((s) => s.leapLookupPending)
  const setActiveKitIndex = useDocumentWizardStore((s) => s.setActiveKitIndex)
  const setLineNonScanQty = useDocumentWizardStore((s) => s.setLineNonScanQty)
  const setLineFromBay = useDocumentWizardStore((s) => s.setLineFromBay)
  const setLineFromBin = useDocumentWizardStore((s) => s.setLineFromBin)
  const setLineToBay = useDocumentWizardStore((s) => s.setLineToBay)
  const setLineShortQty = useDocumentWizardStore((s) => s.setLineShortQty)
  const ingestTag = useDocumentWizardStore((s) => s.ingestTag)
  const removeTagFromLine = useDocumentWizardStore((s) => s.removeTagFromLine)
  const getTagsForLine = useDocumentWizardStore((s) => s.getTagsForLine)

  const scannerConnected = useRfidSessionStore((s) => s.scannerConnected)
  const scannerPending = useRfidSessionStore((s) => s.scannerPending)
  const scannerType = useRfidSessionStore((s) => s.scannerType)
  const mock = isMockScannerEnabled()

  useEffect(() => {
    if (mock) ensureMockConnected()
  }, [mock])

  const sessionActive = isScannerSessionActive({ scannerConnected, scannerPending })
  useScannerTagSubscription(Boolean(document) && (sessionActive || mock))

  const movement = useMemo(
    () => (movementId ? getMovement(movementId) : null),
    [movementId]
  )
  const showFromBay = movement ? movementRequiresFromBay(movement) : false
  const showFromBin = movement ? movementRequiresFromBin(movement) : false
  const showToBay = movement ? movementRequiresToBay(movement) : false
  const showShortQty = movement ? movementShowsShortQty(movement) : false
  const binOptions = useMemo(
    () => RELOCATION_FROM_BINS.map((b) => ({ value: b, label: b })),
    []
  )
  const plant = document
    ? getStorageLocationPlant(document, movementId ?? undefined)
    : ''

  const locationsQ = useQuery({
    queryKey: ['storage-locations', plant],
    queryFn: () => rfidService.fetchStorageLocations(plant),
    enabled: Boolean(plant && (showFromBay || showToBay)),
  })

  const bayOptions = useMemo(
    () => storageLocationDropdownOptions(locationsQ.data ?? []),
    [locationsQ.data]
  )

  const kitFilter = kitGroups.length > 0 ? activeKitIndex : undefined
  const stats = useMemo(
    () => computeScanSessionStats(lines, kitGroups, kitBom, tagSet, kitFilter),
    [lines, kitGroups, kitBom, tagSet, kitFilter]
  )
  const unmatchedLeap = useMemo(
    () => countUnmatchedLeapTags({ tagSet, lines, skuByTag }),
    [tagSet, lines, skuByTag]
  )

  useEffect(() => {
    if (!document || tagSet.size === 0) return
    saveRecentDocumentScan({ status: 'scanning' })
  }, [document, tagSet, lines, kitGroups])

  const onManualSubmit = useCallback(
    (tag: string) => {
      ingestTag(tag)
    },
    [ingestTag]
  )

  const [missingFromBay, setMissingFromBay] = useState<Set<string>>(() => new Set())
  const [missingFromBin, setMissingFromBin] = useState<Set<string>>(() => new Set())
  const [missingToBay, setMissingToBay] = useState<Set<string>>(() => new Set())
  const [detailKey, setDetailKey] = useState<string | null>(null)

  const detailRow = useMemo(() => {
    if (!detailKey) return null
    return stats.lineProgress.find((r) => `${r.kitIndex}-${r.line.id}` === detailKey) ?? null
  }, [detailKey, stats.lineProgress])

  const detailTags = useMemo(() => {
    if (!detailRow) return []
    return getTagsForLine(detailRow.line, detailRow.kitIndex)
  }, [detailRow, getTagsForLine, tagSet, lines, kitGroups])

  if (!hydrated) {
    return <PageLoading tip="Restoring scan session…" />
  }

  if (!document || !movementId || !movement) {
    return <Navigate to="/" replace />
  }

  const goConfirm = () => {
    if (showFromBay || showFromBin || showToBay) {
      const errors = validateMovementLocations(movement, lines, kitGroups)
      if (errors.length > 0) {
        const ids = new Set(errors.map((e) => e.lineId))
        if (showFromBay) setMissingFromBay(ids)
        if (showFromBin) setMissingFromBin(ids)
        if (showToBay) setMissingToBay(ids)
        toast.error(errors[0]?.message ?? 'Bay / bin selection required')
        return
      }
    }
    setMissingFromBay(new Set())
    setMissingToBay(new Set())

    const complete = allScannableComplete(lines, kitGroups, kitBom)
    if (!complete) {
      Modal.confirm({
        title: 'Incomplete scans',
        content:
          'Some scannable lines are not fully covered. You can still continue to review and post.',
        okText: 'Continue anyway',
        cancelText: 'Keep scanning',
        onOk: () => {
          saveRecentDocumentScan({ status: 'review' })
          navigate('/wizard/confirm')
        },
      })
      return
    }
    saveRecentDocumentScan({ status: 'review' })
    navigate('/wizard/confirm')
  }

  const kitIndexForHandlers = kitGroups.length > 0 ? activeKitIndex : 0

  return (
    <DocumentWizardShell
      step={2}
      title={`${movement.legacyName} — Scan`}
      subtitle={`Step 2 of 4 · ${stats.totalTags} tags`}
      onBack={() => navigate('/wizard/capture')}
      document={document}
      footer={
        <WizardNavFooter
          onBack={() => navigate('/wizard/capture')}
          onNext={goConfirm}
          nextLabel="Continue to confirm"
        />
      }
    >
      <div className="rfid-scan-layout">
        <aside className="rfid-scan-layout__aside space-y-3">
          <ScanSessionHero
            totalTags={stats.totalTags}
            overallProgress={stats.overallProgress}
            linesComplete={stats.linesComplete}
            linesTotal={stats.lineProgress.length}
            scannerConnected={sessionActive || mock}
            scannerType={scannerType}
            onConnectScanner={() => navigate('/scanner')}
          />
          <ManualTagEntry onSubmit={onManualSubmit} />
        </aside>

        <div className="min-w-0">
          {leapLookupPending ? (
            <div className="mb-3 rounded-xl border border-border bg-muted/40 px-3 py-2 text-center text-xs font-semibold text-muted-foreground">
              Looking up LEAP tags…
            </div>
          ) : null}
          {!leapLookupPending && unmatchedLeap > 0 ? (
            <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-center text-xs font-semibold text-amber-800">
              {unmatchedLeap} LEAP tag(s) have no SKU on this document
            </div>
          ) : null}
          {kitGroups.length > 0 ? (
            <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
              {kitGroups.map((g, i) => (
                <button
                  key={`${g.kitMaterial}-${i}`}
                  type="button"
                  className={cn(
                    'rfid-kit-tab',
                    activeKitIndex === i && 'rfid-kit-tab--active'
                  )}
                  onClick={() => setActiveKitIndex(i)}
                >
                  {g.kitMaterial || `Kit ${i + 1}`}
                </button>
              ))}
            </div>
          ) : null}

          <div className="rfid-section-label">
            Lines · {stats.linesComplete}/{stats.lineProgress.length} complete
          </div>

          <div className="rfid-line-grid">
            {stats.lineProgress.map((row) => {
              const rowKey = `${row.kitIndex}-${row.line.id}`
              const tags = getTagsForLine(row.line, row.kitIndex)
              return (
                <div
                  key={rowKey}
                  className={cn(
                    (missingFromBay.has(row.line.id) || missingFromBin.has(row.line.id) || missingToBay.has(row.line.id)) &&
                      'rounded-[14px] ring-2 ring-red-400/70'
                  )}
                >
                  <LineItemCard
                    materialCode={row.line.materialCode}
                    materialDesc={row.line.materialDesc}
                    requiredQty={row.required}
                    scanQty={row.scanned}
                    nonScanQty={row.nonScan}
                    status={row.status}
                    scannable={row.line.scannable}
                    fromBay={row.line.fromBay}
                    fromBin={row.line.fromBin}
                    toBay={row.line.toBay}
                    shortQty={row.line.shortQty}
                    showFromBay={showFromBay}
                    showFromBin={showFromBin}
                    showToBay={showToBay}
                    showShortQty={showShortQty}
                    bayOptions={bayOptions}
                    binOptions={binOptions}
                    tags={tags}
                    selected={detailKey === rowKey}
                    onSelect={() => setDetailKey(rowKey)}
                    onRemoveTag={(tag) => removeTagFromLine(row.line.id, tag)}
                    onNonScanChange={
                      row.line.scannable
                        ? (value) =>
                            setLineNonScanQty(row.line.id, value, kitIndexForHandlers)
                        : undefined
                    }
                    onFromBayChange={(value) => {
                      setLineFromBay(row.line.id, value)
                      setMissingFromBay((prev) => {
                        if (!prev.has(row.line.id)) return prev
                        const next = new Set(prev)
                        next.delete(row.line.id)
                        return next
                      })
                    }}
                    onFromBinChange={(value) => {
                      setLineFromBin(row.line.id, value)
                      setMissingFromBin((prev) => {
                        if (!prev.has(row.line.id)) return prev
                        const next = new Set(prev)
                        next.delete(row.line.id)
                        return next
                      })
                    }}
                    onToBayChange={(value) => {
                      setLineToBay(row.line.id, value)
                      setMissingToBay((prev) => {
                        if (!prev.has(row.line.id)) return prev
                        const next = new Set(prev)
                        next.delete(row.line.id)
                        return next
                      })
                    }}
                    onShortQtyChange={(value) =>
                      setLineShortQty(row.line.id, value, kitIndexForHandlers)
                    }
                  />
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <LineTagDetailDrawer
        open={Boolean(detailRow)}
        line={detailRow?.line ?? null}
        tags={detailTags}
        statusLabel={
          detailRow
            ? detailRow.status === 'complete'
              ? 'Complete'
              : detailRow.status === 'in_progress'
                ? 'In progress'
                : 'Pending'
            : undefined
        }
        requiredQty={detailRow?.required}
        scanQty={detailRow?.scanned}
        nonScanQty={detailRow?.nonScan}
        onClose={() => setDetailKey(null)}
        onRemoveTag={(tag) => {
          if (detailRow) removeTagFromLine(detailRow.line.id, tag)
        }}
      />
    </DocumentWizardShell>
  )
}
